// MoscowScoring - MoSCoW framework categorical selection
// Implements US-3.4: MoSCoW Categorical Selection

import React, { useState, useEffect, useCallback } from 'react';
import styles from './MoscowScoring.module.css';
import { Item, Score, CalculatedResult } from '../types/domain';
import { updateScores } from '../services/scoreService';

interface MoscowScoringProps {
  item: Item;
  isReadOnly: boolean;
  onScoresUpdated?: (itemId: string, scores: Score[], result: CalculatedResult) => void;
}

// MOSCOW category options
const MOSCOW_CATEGORIES = [
  { value: '', label: 'Kategorie auswählen...' },
  { value: 'Must Have', label: 'Must Have' },
  { value: 'Should Have', label: 'Should Have' },
  { value: 'Could Have', label: 'Could Have' },
  { value: "Won't Have", label: "Won't Have" },
];

/**
 * MoSCoW categorical selection component
 * - Displays dropdown with 4 category options
 * - Saves immediately on selection (no debouncing needed)
 * - Shows selected category as color-coded badge
 */
export const MoscowScoring: React.FC<MoscowScoringProps> = ({
  item,
  isReadOnly,
  onScoresUpdated,
}) => {
  // EXTRACT current category from item scores
  const getCurrentCategory = (): string => {
    const score = item.scores?.find((s) => s.criterion === 'category');
    return score ? String(score.value) : '';
  };

  // STATE for category selection
  const [category, setCategory] = useState<string>(getCurrentCategory());
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // UPDATE local state when item scores change (real-time updates from other users)
  useEffect(() => {
    setCategory(getCurrentCategory());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.scores]);

  /**
   * SAVE category selection to backend
   * NOTE: No debouncing - saves immediately on selection
   */
  const saveCategory = useCallback(
    async (selectedCategory: string) => {
      // VALIDATE: Empty selection not allowed
      if (!selectedCategory) {
        return;
      }

      try {
        setIsSaving(true);
        setError(null);

        const response = await updateScores(item.id, [
          { criterion: 'category', value: selectedCategory },
        ]);

        if (!response.success) {
          throw new Error(response.error || 'Failed to save category');
        }

        // NOTIFY parent component of score update
        if (onScoresUpdated && response.scores && response.calculatedResult) {
          onScoresUpdated(item.id, response.scores, response.calculatedResult);
        }
      } catch (err) {
        console.error('Failed to save category:', err);
        setError(err instanceof Error ? err.message : 'Failed to save category');
      } finally {
        setIsSaving(false);
      }
    },
    [item.id, onScoresUpdated]
  );

  /**
   * HANDLE dropdown change
   */
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCategory = e.target.value;
    setCategory(selectedCategory);

    // SAVE immediately if not read-only
    if (!isReadOnly && selectedCategory) {
      saveCategory(selectedCategory);
    }
  };

  /**
   * GET category color class for visual distinction
   */
  const getCategoryClass = (cat: string): string => {
    switch (cat) {
      case 'Must Have':
        return styles.categoryMust || '';
      case 'Should Have':
        return styles.categoryShould || '';
      case 'Could Have':
        return styles.categoryCould || '';
      case "Won't Have":
        return styles.categoryWont || '';
      default:
        return '';
    }
  };

  return (
    <div className={styles.moscowScoring}>
      {/* CATEGORY dropdown */}
      <div className={styles.categoryField}>
        <label htmlFor={`category-${item.id}`}>Kategorie</label>
        <select
          id={`category-${item.id}`}
          value={category}
          onChange={handleCategoryChange}
          disabled={isReadOnly || isSaving}
          className={styles.categorySelect}
        >
          {MOSCOW_CATEGORIES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* SELECTED CATEGORY display */}
      {category && (
        <div className={`${styles.categoryDisplay} ${getCategoryClass(category)}`}>
          <span className={styles.categoryLabel}>Zugewiesen:</span>
          <span className={styles.categoryValue}>{category}</span>
          {isSaving && <span className={styles.savingIndicator}>Speichere...</span>}
        </div>
      )}

      {/* ERROR message */}
      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* UNSELECTED indicator */}
      {!category && !isReadOnly && (
        <div className={styles.unscoredMessage}>Wähle eine Kategorie aus, um dieses Thema / Element zu priorisieren</div>
      )}
    </div>
  );
};
